import React from "react";
import { motion } from "framer-motion";
import { Ship, Plane, Truck, Container, FileCheck, Warehouse } from "lucide-react";

const IMG = {
  ship: "/assets/fleet/ship.jpg",
  plane: "/assets/fleet/plane.jpg",
  truck: "/assets/fleet/truck.jpg",
  container: "/assets/fleet/container.jpg",
  customs: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  warehousing: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const SERVICES = [
  { id: "sea-freight", title: "Sea Freight", Icon: Ship, desc: "FCL & LCL ocean shipping from JNPT, Mundra & Chennai to 120+ global ports.", img: IMG.ship, span: "lg:col-span-2 lg:row-span-2", testId: "service-card-sea-freight" },
  { id: "air-freight", title: "Air Freight", Icon: Plane, desc: "Time-critical air cargo & perishables express.", img: IMG.plane, span: "lg:col-span-1", testId: "service-card-air-freight" },
  { id: "road-transport", title: "Road Transport", Icon: Truck, desc: "Pan-India first & last-mile trucking network.", img: IMG.truck, span: "lg:col-span-1", testId: "service-card-road-transport" },
  { id: "container", title: "Container Handling", Icon: Container, desc: "Reefer, dry & specialised container solutions with real-time tracking.", img: IMG.container, span: "lg:col-span-1", testId: "service-card-container" },
  { id: "customs", title: "Customs Clearance", Icon: FileCheck, desc: "End-to-end documentation, compliance & duty optimisation.", img: IMG.customs, span: "lg:col-span-1", testId: "service-card-customs" },
  { id: "warehousing", title: "Warehousing", Icon: Warehouse, desc: "Bonded storage, packaging & inventory management.", img: IMG.warehousing, span: "lg:col-span-1", testId: "service-card-warehousing" },
];

const card = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Services() {
  return (
    <section id="services" className="relative mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
      <div className="mb-14 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="data-label">02 — Capabilities</span>
          <h2 className="mt-3 font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl lg:text-5xl">
            Multimodal freight,<br /><span className="text-[#E65100]">one gateway.</span>
          </h2>
        </div>
        <p className="max-w-sm text-sm text-slate-300 sm:text-base">
          Every mode of transport, orchestrated under a single logistics command — engineered for Indian exporters going global.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:auto-rows-[220px]">
        {SERVICES.map((s, i) => (
          <motion.div
            key={s.id}
            data-testid={s.testId}
            variants={card}
            custom={i}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className={`hover-lift group relative overflow-hidden rounded-2xl border border-white/20 bg-[#111C2E] p-6 hover:border-[#E65100] hover:shadow-[0_15px_45px_rgba(230,81,0,0.3)] ${s.span}`}
          >
            {s.img && (
              <>
                <img
                  src={s.img}
                  alt={s.title}
                  className="absolute inset-0 h-full w-full object-cover opacity-60 transition-all duration-700 brightness-110 group-hover:scale-110 group-hover:opacity-85 group-hover:brightness-125"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B1320] via-[#0B1320]/50 to-transparent transition-opacity duration-500 group-hover:via-[#0B1320]/30" />
              </>
            )}
            <div className="relative z-10 flex h-full flex-col justify-between">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl border border-white/20 bg-[#0B1320]/80 backdrop-blur-md">
                <s.Icon className="h-6 w-6 text-[#FFD700]" />
              </div>
              <div className="rounded-xl bg-[#0B1320]/80 p-3.5 backdrop-blur-md border border-white/10">
                <h3 className="font-display text-xl font-bold text-white sm:text-2xl drop-shadow-md">{s.title}</h3>
                <p className="mt-1 text-xs text-slate-200 sm:text-sm font-medium">{s.desc}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
